import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { Text, Stack } from '@servicetitan/design-system';
import { RouteParams } from '../signature-routes';
import { lazyInject } from 'src/signature/ioc';
import { observer } from 'mobx-react';
import { SignatureStore, SignatureDocument } from 'src/signature/stores/signature.store';
import { SignModalStore } from 'src/signature/stores/sign-modal.store';
import { FormSignatureRequestModel } from 'src/api/model/formSignatureRequestModel';
import { InvoiceSignatureRequestModel } from 'src/api/model/invoiceSignatureRequestModel';
import { ProjectSignatureRequestModel } from 'src/api/model/projectSignatureRequestModel';
import { signatureTypeEnumToString } from 'src/signature/util/signatureTypeEnumToString';
import {
    isInvoiceSignature,
    isFormSignature,
    isProjectSignature,
    getDocumentPdfUrl,
} from 'src/signature/util/document-utils';
import { DocumentDetailsCard } from './document-details-card';
import { VisitAssistantHeader } from '../visit-assistant-header/visit-assistant-header';
import { PageContainer } from '../page-container/page-container';
import pageStyles from '../signature-page/signature-page.less';
import { Signature } from '../sign-modal/signature';
import { disclaimerTypeEnumToString } from 'src/signature/util/disclaimerTypeEnumToString';

interface DocumentDetailsState {
    open: boolean;
}

interface DocumentData {
    header?: string;
    content?: string;
    isDocumentSigned?: boolean;
    termsFileName?: string;
}

@observer
class DocumentDetailsUnwrapped extends React.Component<
    RouteComponentProps<RouteParams>,
    DocumentDetailsState
> {
    @lazyInject(SignatureStore) private signatureStore!: SignatureStore;
    @lazyInject(SignModalStore) private signModalStore!: SignModalStore;

    constructor(props: RouteComponentProps<RouteParams>) {
        super(props);
        this.state = {
            open: false,
        };

        this.init();
    }

    init = async () => {
        const { history, match } = this.props;
        const { visitActionId } = match.params;
        const { loadDocuments, documents } = this.signatureStore;

        if (!documents.length) {
            await this.signatureStore.getVisitActionRequest(visitActionId);
            this.signModalStore.setVisitActionId(visitActionId!);
            await loadDocuments();
            if (!this.signatureStore.documents.length) {
                history.push(`/signatures/${visitActionId}`);
            }
        }
    };

    handleClickSign = () => {
        const { history, match } = this.props;
        const { signatureType, visitActionId, disclaimerId, invoiceId, projectId } = match.params;
        const isInvoice = invoiceId != null;
        const isProject = projectId != null;
        if (disclaimerId != null && (isInvoice || isProject)) {
            const document = this.getDocument() as SignatureDocument;
            const nextDisclaimer = Number(disclaimerId) + 1;
            const docTypePath = isInvoice ? 'invoice-signature' : 'project-signature';
            const basePath = `/signatures/${visitActionId}/${docTypePath}/${invoiceId}/${signatureType}`;
            if (nextDisclaimer < document.disclaimers.length) {
                history.push(`${basePath}/${nextDisclaimer}`);
            } else {
                history.push(basePath);
            }
        } else {
            this.setState({ open: true });
        }
    };

    handleClose = () => {
        this.setState({ open: false });
        this.signModalStore.setIsSubmitSuccess(false);
    };

    getDocument = (): SignatureDocument | undefined => {
        const { match } = this.props;
        const { invoiceId, projectId, submittingId, signatureType } = match.params;
        const { documents } = this.signatureStore;

        if (signatureType) {
            if (invoiceId) {
                return documents.find((document: SignatureDocument) => {
                    const doc = document as InvoiceSignatureRequestModel;
                    return (
                        doc.invoiceId === Number(invoiceId) &&
                        Number(signatureType) === doc.signatureType
                    );
                });
            }

            if (projectId) {
                return documents.find((document: SignatureDocument) => {
                    const doc = document as ProjectSignatureRequestModel;
                    return (
                        doc.projectId === Number(projectId) &&
                        Number(signatureType) === doc.signatureType
                    );
                });
            }
        }

        if (submittingId) {
            return documents.find(
                (document) =>
                    (document as FormSignatureRequestModel).submittingId === Number(submittingId)
            );
        }
    };

    getDocumentData = (document?: SignatureDocument): DocumentData => {
        const { match } = this.props;
        const { disclaimerId } = match.params;
        if (isInvoiceSignature(document) || isProjectSignature(document)) {
            const disclaimer = document.disclaimers[Number(disclaimerId)];
            return {
                header: disclaimer
                    ? disclaimerTypeEnumToString(disclaimer.disclaimerType)
                    : signatureTypeEnumToString(document.signatureType),
                content: disclaimer ? disclaimer.legalText : document.legalText,
                isDocumentSigned: !!document.signedOn,
                termsFileName: document.termsFileName,
            };
        }

        if (isFormSignature(document)) {
            return {
                header: document.formName,
                isDocumentSigned: !!document.signedOn,
            };
        }

        return {};
    };

    render() {
        const { open } = this.state;
        const { businessUnitOfficialName, documents } = this.signatureStore;
        const { visitActionId, disclaimerId } = this.props.match.params;

        if (!documents.length) {
            return null;
        }

        const document = this.getDocument() as SignatureDocument;
        const documentPdfUrl = getDocumentPdfUrl(visitActionId, document);
        const { header, content, isDocumentSigned, termsFileName } = this.getDocumentData(document);
        const showSignModal = !isDocumentSigned || open;

        return (
            <PageContainer>
                <VisitAssistantHeader showTenantName tenantName={businessUnitOfficialName} />
                {}
                <Stack direction="column" className={pageStyles.pageContent}>
                    <Text bold size={6} className="p-t-5 p-b-3">
                        View and Sign Document
                    </Text>
                    <DocumentDetailsCard
                        header={header}
                        content={content}
                        documentPdfUrl={documentPdfUrl}
                        isDocumentSigned={isDocumentSigned}
                        termsFileName={termsFileName}
                        onClickSign={this.handleClickSign}
                        isDisclaimer={disclaimerId != null}
                    />
                </Stack>
                {}

                {}

                {showSignModal && (
                    <Signature document={document} open={open} handleClose={this.handleClose} />
                )}
            </PageContainer>
        );
    }
}

export const DocumentDetails = withRouter(DocumentDetailsUnwrapped);
